import { useCallback, useState } from 'react';
import { useNotifications } from '@decisiv/ui-components';
import { fetchUsers, toggleLimitedRole } from '../services';

function useUsers(csrfToken) {
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const { notify } = useNotifications();

  const notifyError = useCallback(
    (error) => {
      notify({
        id: 'notify-error',
        intent: 'danger',
        title: error,
        duration: 15000,
        onClose: () => {},
      });
    },
    [notify],
  );

  const getUsers = useCallback(
    async (accountId, query, page, sortBy, sortOrder) => {
      setLoading(true);
      try {
        const response = await fetchUsers(
          csrfToken,
          accountId,
          query,
          page,
          sortBy,
          sortOrder,
        );
        const parsedResponse = await response.json();
        setUsers(parsedResponse.users);
        setTotalPages(parsedResponse.total_pages);
      } catch (error) {
        console.error('error', error);
        notifyError(error);
      } finally {
        setLoading(false);
      }
    },
    [csrfToken, notifyError],
  );

  const toggleRole = useCallback(
    async (accountId, userId) => {
      try {
        const response = await toggleLimitedRole(csrfToken, accountId, userId);
        const parsedResponse = await response.json();
        setUsers((current) =>
          current.map((user) =>
            user.id === userId
              ? { ...user, has_limited_role: parsedResponse.has_limited_role }
              : user,
          ),
        );
        notify({
          id: 'notify-success',
          intent: 'success',
          title: parsedResponse.message,
          duration: 5000,
          onClose: () => {},
        });
      } catch (error) {
        console.error('error', error);
        notifyError(error);
      }
    },
    [csrfToken, notify, notifyError],
  );

  const clearUsers = useCallback(() => {
    setUsers([]);
    setTotalPages(0);
  }, []);

  return {
    getUsers,
    clearUsers,
    toggleRole,
    loading,
    users,
    totalPages,
  };
}

export default useUsers;
